import { Injectable } from '@angular/core';
import { ActionsModalsService } from '@app-services/emails/button-actions/actions-modal.service';
import { ActionsService } from '@app-services/emails/button-actions/actions.service';
import { TransformEmailListItemContract } from '@app-types/api/email';
import { BaseGetByIdRequest } from '@app-types/base/base';
import { EmailActionsContextService } from '@app-services/emails/button-actions/email-actions-context.service';
import { EmailClient } from '@app-services/api/clients/email-client';
import { MatchError } from '@app-services/errors/error-matcher';
import { MailFolderSelectDialogComponent } from '@app-components/mail-account-folders/mail-folder-select-dialog/mail-folder-select-dialog.component';
import { EmailsPageStateService } from '@app-services/emails/emails-page-state.service';

@Injectable({
  providedIn: 'root',
})
export class ButtonActionsService {
  private readonly emailClient: EmailClient;
  private readonly matchError: MatchError;
  private readonly emailsPageStateService: EmailsPageStateService;

  constructor(
    public actionsModals: ActionsModalsService,
    public actions: ActionsService,
    private context: EmailActionsContextService
  ) {
    this.emailClient = context.emailClient;
    this.matchError = context.matchError;
    this.emailsPageStateService = context.emailsPageStateService;
  }

  async reply(email?: TransformEmailListItemContract): Promise<void> {
    const fromHeader = !email;
    const needEmail = email
      ? email
      : this.emailsPageStateService.getEmails([
          this.emailsPageStateService.selection.selected[0],
        ])[0];

    try {
      this.emailsPageStateService.updateLoadingButtons({
        replyingEmailId: needEmail.emailId,
        loadingActionFromHeader: fromHeader,
      });

      const newEmail = await this.emailClient.reply({
        emailId: needEmail.emailId,
      });
      const replyEmail = {
        ...needEmail,
        emailId: newEmail.id,
        parentEmailId: needEmail.emailId,
      };

      this.actionsModals.openCreateOrUpdateModalForm(replyEmail);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.actions.emailsPageStateService.updateLoadingButtons({
        replyingEmailId: null,
        loadingActionFromHeader: false,
      });
    }
  }

  async replyAll(email?: TransformEmailListItemContract): Promise<void> {
    const fromHeader = !email;
    const needEmail = email
      ? email
      : this.emailsPageStateService.getEmails([
          this.emailsPageStateService.selection.selected[0],
        ])[0];

    try {
      this.actions.emailsPageStateService.updateLoadingButtons({
        replyingAllEmailId: needEmail.emailId,
        loadingActionFromHeader: fromHeader,
      });
      const newEmail = await this.emailClient.replyAll({
        emailId: needEmail.emailId,
      });
      const replyEmail = {
        ...needEmail,
        emailId: newEmail.id,
        parentEmailId: needEmail.emailId,
      };

      this.actionsModals.openCreateOrUpdateModalForm(replyEmail);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.actions.emailsPageStateService.updateLoadingButtons({
        replyingAllEmailId: null,
        loadingActionFromHeader: false,
      });
    }
  }

  async forward(id: number | null = null): Promise<void> {
    const fromHeader = id === null;
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    if (ids.length !== 1) {
      return this.forwardAsAttachment(id);
    }

    try {
      this.actions.emailsPageStateService.updateLoadingButtons({
        forwardingAsTextEmailId: ids[0],
        loadingActionFromHeader: fromHeader,
      });

      const createResponse = await this.emailClient.forward({
        emailId: ids[0],
      });

      const newEmailResponse = await this.emailClient.getById(
        new BaseGetByIdRequest(createResponse?.id ?? null)
      );
      this.actionsModals.openCreateOrUpdateModalForm(newEmailResponse.email);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.actions.emailsPageStateService.updateLoadingButtons({
        forwardingAsTextEmailId: null,
        loadingActionFromHeader: false,
      });
    }
  }

  async forwardAsAttachment(id: number | null = null): Promise<void> {
    const fromHeader = id === null;
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    try {
      this.actions.emailsPageStateService.updateLoadingButtons({
        forwardingAsAttachmentEmailId: ids[0],
        loadingActionFromHeader: fromHeader,
      });

      const createResponse = await this.emailClient.forwardMultiple({
        emailIds: ids,
        mailAccountId:
          this.emailsPageStateService.infoOpenedMailAccount?.acc ?? 0,
      });

      const newEmailResponse = await this.emailClient.getById(
        new BaseGetByIdRequest(createResponse?.id ?? null)
      );
      this.actionsModals.openCreateOrUpdateModalForm(newEmailResponse.email);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.actions.emailsPageStateService.updateLoadingButtons({
        forwardingAsAttachmentEmailId: null,
        loadingActionFromHeader: false,
      });
    }
  }

  async restoreMoveFolderChoose(
    ids: number[],
    operationType: string
  ): Promise<any> {
    this.actionsModals.openDialog(
      MailFolderSelectDialogComponent,
      {
        width: '350px',
        autoFocus: false,
        data: {
          mailAccountId: this.emailsPageStateService.infoOpenedMailAccount.acc,
          ids,
          isRestore: true,
          folderType: this.emailsPageStateService.infoOpenedMailAccount.type,
          title: 'Select the folder to restore',
        },
      },
      (e: { confirmed: boolean; folderId: number }) => {
        if (e?.confirmed) {
          if (operationType === 'restoreOneDeleted') {
            this.actions.restore(e.folderId, ids);
          }
          if (operationType === 'restoreOneSpam') {
            this.actions.setNotSpam(ids, e.folderId);
          }
          if (operationType === 'restoreSelectedDeleted') {
            this.actions.restore(e.folderId);
          }
        }
      }
    );
  }
}
